import { useMemo } from "react"
import { Button, Flex, Image } from "@chakra-ui/react"
import { SupportedTokenName } from "@mezo/sdk"
import { LabelLarge } from "@mezo-org/mezo-clay"

import checkmark from "#/assets/checkmark.svg"
import emptyCircleWhite from "#/assets/empty-circle-white.svg"
import {
  selectDepositActionCurrentLockDuration,
  selectDepositActionLockEnd,
  useDappSelector,
} from "#/redux-state"
import {
  calculateDailyMats,
  calculateWeeksLockTimeLeft,
  formatMats,
} from "#/shared/utils"

import DepositBoost from "./DepositBoost"

type DepositDurationBoxProps = {
  isActive: boolean
  duration: number
  multiplier: number
  onClick: (duration: number) => void
  userAmount: string
  token: SupportedTokenName
  extendBoost?: boolean
}

export default function DepositDurationBox({
  isActive,
  duration,
  multiplier,
  onClick,
  userAmount,
  token,
  extendBoost = false,
}: DepositDurationBoxProps) {
  const currentLockDuration = useDappSelector(
    selectDepositActionCurrentLockDuration,
  )

  const lockEnd = useDappSelector(selectDepositActionLockEnd)
  const weeksTimeLeft = calculateWeeksLockTimeLeft(new Date(lockEnd))

  const dailyMats = calculateDailyMats(userAmount, multiplier, token)
  const formattedDailyMats = formatMats(dailyMats)

  const isDisabled = useMemo(
    () => extendBoost && !!currentLockDuration && duration < weeksTimeLeft,
    [currentLockDuration, weeksTimeLeft, extendBoost, duration],
  )

  const durationInMonths = useMemo(() => Math.floor(duration / 4), [duration])

  return (
    <Button
      w="100%"
      rounded="8px"
      borderWidth={1}
      borderStyle="solid"
      borderColor={isActive ? "primary.black.100" : "primary.black.50"}
      bg={isActive ? "primary.black.100" : "transparent"}
      _hover={{ bg: isActive ? "primary.black.90" : "primary.black.5" }}
      onClick={() => onClick(duration)}
      isDisabled={isDisabled}
    >
      <Flex w="100%" justifyContent="space-between" alignItems="center">
        <Flex p={4} gap={4} w="100%" alignItems="center">
          <Flex w="24px" justifyContent="center">
            {isActive ? (
              <Image src={checkmark} />
            ) : (
              <Image src={emptyCircleWhite} />
            )}
          </Flex>
          <LabelLarge color={isActive ? "white" : "black"}>
            {durationInMonths} {durationInMonths > 1 ? "Months" : "Month"}
          </LabelLarge>
        </Flex>
        <DepositBoost
          dailyMats={formattedDailyMats}
          isActive={isActive}
          multiplier={multiplier}
          extendBoost={extendBoost}
        />
      </Flex>
    </Button>
  )
}
